import React, { useCallback, useEffect, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import MainLayout from '../../layouts/MainLayout';
import { AppDispatch } from '../../../index';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'antd/es/form/Form';
import { useDebounce } from 'react-use';
import { Breadcrumb, Button, Form, Input, Row, Space, Tag, Typography } from 'antd';
import { EditOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import LoaderWrapper from '../../atoms/LoaderWrapper';
import tileStyles from '../../../styles/tile.module.css';
import { useDeviceGroups, useDeviceGroupsState } from '../../../features/deviceGroups/deviceGroups.hooks';
import { createDeviceGroup, fetchDeviceGroups } from '../../../features/deviceGroups/deviceGroups.state';

const emptyEntity = { name: '' };

const DeviceGroupsPage: React.FC = () => {
  const groups = useDeviceGroups();
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const groupsState = useDeviceGroupsState();

  const [name, setName] = useState<string | undefined>(undefined);
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<{ name: string }>(emptyEntity);
  const [form] = useForm();

  const handleFormSubmit = useCallback(async () => {
    const response = await dispatch(createDeviceGroup(newEntity));

    if (createDeviceGroup.fulfilled.match(response)) {
      history.push(`/testing/groups/${response.payload.id}`);
    }
  }, [dispatch, history, newEntity]);

  useDebounce(
    () => {
      if (name !== undefined) {
        dispatch(fetchDeviceGroups({ params: { name: name } }));
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/testing/groups'>
                <Space>
                  <ProfileTwoTone />
                  Группы устройств
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            Группы устройств
          </Typography.Title>
          <div>
            <Input
              autoFocus
              allowClear
              placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
              prefix={<SearchOutlined />}
              onChange={e => {
                setName(e.target.value);
              }}
              style={{ marginBottom: 10 }}
            />
            <LoaderWrapper loading={groupsState.isPending}>
              <div className={styles.content}>
                {createMode && (
                  <Form
                    form={form}
                    validateTrigger='onBlur'
                    layout='vertical'
                    className={tileStyles.listTile}
                    onFinish={handleFormSubmit}
                  >
                    <Form.Item name='name' noStyle>
                      <Input
                        autoFocus
                        type='text'
                        name='name'
                        placeholder='Введите название группы'
                        onChange={ev => setNewEntity(e => ({ ...e, name: ev.target.value }))}
                      />
                    </Form.Item>
                    <Button
                      style={{ marginLeft: 10 }}
                      htmlType='submit'
                      type='primary'
                      disabled={newEntity.name === ''}
                    >
                      Создать
                    </Button>
                    <Button
                      style={{ marginLeft: 10 }}
                      htmlType='button'
                      type='default'
                      onClick={() => setCreateMode(false)}
                    >
                      Отменить
                    </Button>
                  </Form>
                )}
                {groups.length === 0 && (
                  <Row justify='center' style={{ width: '100%', margin: '16px 0' }}>
                    <Typography.Text>
                      <FormattedMessage id={'text.NothingFound'} />
                    </Typography.Text>
                  </Row>
                )}
                {groups.map(c => (
                  <div key={c.id} className={tileStyles.listTile}>
                    <Space direction={'vertical'}>
                      <Link to={`/testing/groups/${c.id}`} className={tileStyles.tileName}>
                        <Space>
                          <ProfileTwoTone />
                          {c.name}
                        </Space>
                      </Link>
                      <Space>
                        <Typography.Text>Шаблон: </Typography.Text>
                        {c.templateId ? (
                          <Link to={`/content/templates/${c.templateId}/groups`}>{c.templateName}</Link>
                        ) : (
                          '-'
                        )}
                      </Space>
                    </Space>
                    <Space>
                      {c.disabled && <Tag>Заблокировано</Tag>}
                      <Button>
                        <EditOutlined />
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
            </LoaderWrapper>
          </div>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={() => setCreateMode(true)}>
              Создать новую группу
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const DeviceGroupsPageBlock: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchDeviceGroups({}));
  }, [dispatch]);

  return <DeviceGroupsPage />;
};

export default DeviceGroupsPageBlock;
